import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

// ALWAYS use relative URL - same-origin requests only
const API = '/api';

const Scanner = ({ user: propsUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(propsUser);
  const [loading, setLoading] = useState(false);
  
  const [adText, setAdText] = useState('');
  const [landingPageCopy, setLandingPageCopy] = useState('');
  const [targetJurisdictions, setTargetJurisdictions] = useState([]);
  const [targetPlatforms, setTargetPlatforms] = useState([]);

  useEffect(() => {
    if (!propsUser) {
      axios.get(`${API}/auth/me`, { withCredentials: true })
        .then(res => setUser(res.data))
        .catch(err => console.error('Failed to load user', err));
    }
  }, [propsUser]);

  const handleScan = async (e) => {
    e.preventDefault();
    
    if (targetJurisdictions.length === 0 || targetPlatforms.length === 0) {
      toast.error('Please select at least one jurisdiction and platform');
      return;
    }

    if (!adText.trim()) {
      toast.error('Please enter your ad text');
      return;
    }

    setLoading(true);

    // Combine ad text with landing page copy if provided
    const fullAdText = landingPageCopy.trim() 
      ? `${adText}\n\n--- Landing Page Copy ---\n${landingPageCopy}`
      : adText;

    try {
      const response = await axios.post(
        `${API}/scan`,
        {
          ad_text: fullAdText,
          target_jurisdictions: targetJurisdictions,
          target_platforms: targetPlatforms
        },
        { withCredentials: true }
      );

      toast.success(`Compliance scan completed!`);
      toast.info(`Analyzed across ${targetJurisdictions.length} jurisdiction(s) and ${targetPlatforms.length} platform(s)`);
      
      navigate(`/scan/${response.data.scan_id}`);
      
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Scan failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const adTextPlaceholder = `Example:
Doctors don't want you to know this fat-loss trick.
Burn stubborn belly fat fast without diet or exercise.
Limited stock available — order today!

Paste your ad copy here...`;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar user={user} />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8">
            <h1 className="font-heading text-4xl font-bold mb-2" data-testid="scanner-title">Compliance Scanner</h1>
            <p className="text-muted-foreground">Analyze your ad copy for regulatory and platform compliance risks</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ad Content</CardTitle>
              <CardDescription>Paste your advertising copy for AI-powered compliance analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleScan} className="space-y-6">
                {/* Ad Text - Required */}
                <div>
                  <Label htmlFor="adText">Ad Text *</Label>
                  <Textarea
                    id="adText"
                    placeholder={adTextPlaceholder}
                    value={adText}
                    onChange={(e) => setAdText(e.target.value)}
                    rows={6}
                    className="font-mono text-sm"
                    data-testid="ad-text-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste your complete ad copy including headlines, body text, and call-to-action
                  </p>
                </div>

                {/* Landing Page Copy - Optional */}
                <div>
                  <Label htmlFor="landingPageCopy">Landing Page Copy (optional)</Label>
                  <Textarea
                    id="landingPageCopy"
                    placeholder="Paste the text content from your landing page here (optional)..."
                    value={landingPageCopy}
                    onChange={(e) => setLandingPageCopy(e.target.value)}
                    rows={4}
                    className="font-mono text-sm"
                    data-testid="landing-page-copy-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Include any claims, testimonials, or disclaimers from your landing page
                  </p>
                </div>

                {/* Target Jurisdictions - Multi-select */}
                <div>
                  <Label htmlFor="jurisdictions">Target Jurisdictions * (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {['Canada', 'US', 'EU', 'India'].map((jurisdiction) => (
                      <div key={jurisdiction} className="flex items-center space-x-2 p-3 border rounded-md hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          id={`jurisdiction-${jurisdiction}`}
                          checked={targetJurisdictions.includes(jurisdiction)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTargetJurisdictions([...targetJurisdictions, jurisdiction]);
                            } else {
                              setTargetJurisdictions(targetJurisdictions.filter(j => j !== jurisdiction));
                            }
                          }}
                          className="h-4 w-4"
                          data-testid={`jurisdiction-checkbox-${jurisdiction.toLowerCase()}`}
                        />
                        <label htmlFor={`jurisdiction-${jurisdiction}`} className="text-sm font-medium cursor-pointer flex-1">
                          {jurisdiction === 'US' ? 'United States' : jurisdiction === 'EU' ? 'European Union' : jurisdiction}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Selected: {targetJurisdictions.length > 0 ? targetJurisdictions.join(', ') : 'None'}
                  </p>
                </div>

                {/* Target Platforms - Multi-select */}
                <div>
                  <Label htmlFor="platforms">Target Platforms * (Select all that apply)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {[
                      { value: 'Meta', label: 'Meta (Facebook/Instagram)' },
                      { value: 'Google', label: 'Google Ads' },
                      { value: 'TikTok', label: 'TikTok' },
                      { value: 'Amazon', label: 'Amazon' },
                      { value: 'LinkedIn', label: 'LinkedIn' },
                      { value: 'YouTube', label: 'YouTube' }
                    ].map((platform) => (
                      <div key={platform.value} className="flex items-center space-x-2 p-3 border rounded-md hover:bg-muted/50 cursor-pointer">
                        <input
                          type="checkbox"
                          id={`platform-${platform.value}`}
                          checked={targetPlatforms.includes(platform.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setTargetPlatforms([...targetPlatforms, platform.value]);
                            } else {
                              setTargetPlatforms(targetPlatforms.filter(p => p !== platform.value));
                            }
                          }}
                          className="h-4 w-4"
                          data-testid={`platform-checkbox-${platform.value.toLowerCase()}`}
                        />
                        <label htmlFor={`platform-${platform.value}`} className="text-sm font-medium cursor-pointer flex-1">
                          {platform.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Selected: {targetPlatforms.length > 0 ? targetPlatforms.join(', ') : 'None'}
                  </p>
                </div>

                {/* Scan Limit Warning */}
                {user && (
                  <div className="bg-muted p-4 rounded-md">
                    <p className="text-sm text-muted-foreground">
                      Scans used: <span className="font-medium text-foreground">{user.scans_used} / {user.scan_limit}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      This will count as <strong>1 scan</strong> regardless of jurisdictions/platforms selected
                    </p>
                  </div>
                )}

                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                  data-testid="scan-submit-btn"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Analyzing across {targetJurisdictions.length}x{targetPlatforms.length}...
                    </>
                  ) : (
                    <>
                      <Shield className="mr-2 h-5 w-5" />
                      Scan for Compliance {targetJurisdictions.length > 0 && targetPlatforms.length > 0 && `(1 scan for ${targetJurisdictions.length}x${targetPlatforms.length} coverage)`}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="mt-6 border-accent border-2 bg-accent/5">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <AlertCircle className="h-6 w-6 text-accent flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p className="text-sm font-bold text-accent">IMPORTANT: NOT LEGAL ADVICE</p>
                  <p className="text-sm text-foreground">
                    ScanMyAd provides AI-assisted compliance intelligence and <strong>does NOT constitute legal advice</strong>. 
                    Regulatory interpretations vary by jurisdiction and change over time.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <strong>You must:</strong> Consult qualified legal counsel before publishing ads | Verify all analysis with official regulatory sources | 
                    Test ads according to platform guidelines | Maintain substantiation records for all claims
                  </p>
                  <p className="text-xs text-muted-foreground italic">
                    <strong>Liability:</strong> You are solely responsible for advertising compliance. ScanMyAd is not liable for fines, 
                    account suspensions, or damages arising from this analysis.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Scanner;
